import { BigNumber } from '@ethersproject/bignumber'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { Balance, Gas, Pool, TotalValueLocked } from '../../common/icons'
import {
  Borders,
  Colors,
  FontWeights,
  INTEGRAL_SYMBOL,
  WETH_WBTC,
  WETH_USDC,
  WETH_USDT,
  WETH_DAI,
  WETH_LINK,
  DAI_USDC,
  DAI_USDT,
  USDC_USDT,
} from '../../constants'
import { Currency, CurrencyValue, formatCurrency, Pair } from '../../model'
import { useApiPools, useApiTokenPrices, useApiTokenRewards } from '../../providers'
import { GasRebatesTooltip } from './GasRebatesTooltip'
import { TotalValueLockedTooltip } from './TotalValueLockedTooltip'
import { AccruedLpRewardsTooltip } from './AccruedLpRewardsTooltip'
import { LpRewardsTooltip } from './LpRewardsTooltip'
import { PercentOfPoolTooltip } from './PercentOfPoolTooltip'
import { PoolRatioTooltip } from './PoolRatioTooltip'
import {
  BN_ZERO,
  BN_10_18,
  numberTo18Decimals,
  bigNumber18DecimalsToDollars,
  bigNumber18DecimalsToPercent,
} from '../../utils'
import { Pool as PoolModel } from '../../providers/apiPools/model'
import { TokenPrices } from '../../providers/apiTokenPrices/model'
import { formatPoolMinimumTVL, normalizeTo18Decimals } from '../../utils'

const GAS_REBATE_VOLUME = 30000
const POOL_BALANCE_LOWER_THRESHOLD_DEFAULT = 0
const POOL_BALANCE_UPPER_THRESHOLD_DEFAULT = 100

interface Props {
  pair?: Pair
  firstValue: CurrencyValue
  secondValue: CurrencyValue
  liquidityBalance: BigNumber
  totalLiquiditySupply: BigNumber
}

type DepositSwap = {
  volume: BigNumber
  swapInToken: Currency
  swapOutToken: Currency
}

export function AddLiquidityInfo({ pair, firstValue, secondValue, liquidityBalance, totalLiquiditySupply }: Props) {
  const apiTokenPrices = useApiTokenPrices()
  const apiPools = useApiPools()
  const apiPool = pair && apiPools && apiPools[pair.address.toLowerCase()]
  const apiTokenRewards = useApiTokenRewards()

  const lpRewards = useMemo(
    () => pair && apiTokenRewards && apiTokenRewards.lpRewards[pair.address.toLowerCase()],
    [pair, apiTokenRewards]
  )

  const poolMinimumTVL = getPoolMinimumTVL(pair)
  const poolBalanceThresholds = getPoolBalanceThresholds(pair)
  const [aprDisabled, apr, aprAction] = calculateAprDisplay(pair, apiPool, poolBalanceThresholds, poolMinimumTVL)
  const depositSwap = calculateDepositSwap(firstValue, secondValue, pair, apiPool, apiTokenPrices)
  const gasRebateDisplay = calculateGasRebateDisplay(pair, depositSwap, apiPool)

  return (
    <Content>
      <Container>
        <Left>
          <ul>
            <li>
              <LeftLabel>
                LP Rewards
                <LpRewardsTooltip poolBalanceThresholds={poolBalanceThresholds} poolMinimumTVL={poolMinimumTVL} />
              </LeftLabel>
              <BigValue warning={aprDisabled}>{apr}</BigValue>
              <Small warning={aprDisabled}>{aprAction}</Small>
            </li>
            <li>
              <LeftLabel>
                Accrued Rewards
                <AccruedLpRewardsTooltip />
              </LeftLabel>
              <Value>
                {lpRewards ? formatCurrency(18, lpRewards.tokenRewards.total, 2) : '-'} {INTEGRAL_SYMBOL}
              </Value>
            </li>
          </ul>
        </Left>
        <Right>
          <ul>
            <li>
              <Label>
                <TotalValueLocked /> TVL
              </Label>
              <Value>
                {apiPool ? bigNumber18DecimalsToDollars(apiPool.totalTokenValue) : '-'}
                <TotalValueLockedTooltip pair={pair} apiPool={apiPool} />
              </Value>
            </li>
            <li>
              <Label>
                <Balance /> Pool Ratio
              </Label>
              <Value>
                {`${apiPool ? bigNumber18DecimalsToPercent(apiPool.tokenValueRatio) : '-'} ${
                  pair ? pair.getToken0Symbol() : '-'
                } / ${apiPool ? bigNumber18DecimalsToPercent(BN_10_18.sub(apiPool.tokenValueRatio)) : '-'} ${
                  pair ? pair.getToken1Symbol() : '-'
                }`}
                <PoolRatioTooltip />
              </Value>
            </li>
            <li>
              <Label>
                <Pool /> Pool %
              </Label>
              <Value>
                {liquidityBalance.isZero() || totalLiquiditySupply.isZero()
                  ? '-'
                  : `${formatCurrency(18, liquidityBalance.mul(BN_10_18).mul(100).div(totalLiquiditySupply), 2)}%`}
                <PercentOfPoolTooltip liquidityBalance={liquidityBalance} totalLiquiditySupply={totalLiquiditySupply} />
              </Value>
            </li>
            <li>
              <Label>
                <Gas /> Gas Rebate
              </Label>
              <Value>
                {gasRebateDisplay}
                <GasRebatesTooltip poolBalanceThresholds={poolBalanceThresholds} />
              </Value>
            </li>
          </ul>
        </Right>
      </Container>
    </Content>
  )
}

function calculateAprDisplay(
  pair?: Pair,
  apiPool?: PoolModel,
  poolBalanceThresholds?: number[],
  poolMinimumTVL?: number
): [boolean, string, string] {
  let disabled = false
  let apr = '-'
  let action = ''

  if (pair && apiPool) {
    if (poolMinimumTVL !== undefined && poolBalanceThresholds) {
      apr = `${bigNumber18DecimalsToPercent(apiPool.apr)} APR`
      const isBalanced =
        apiPool.tokenValueRatio.gte(numberTo18Decimals(poolBalanceThresholds[0]).div(100)) &&
        apiPool.tokenValueRatio.lte(numberTo18Decimals(poolBalanceThresholds[1]).div(100))
      const hasEnoughTvl = apiPool.totalTokenValue.gte(numberTo18Decimals(poolMinimumTVL))
      if (!isBalanced || !hasEnoughTvl) {
        disabled = true
        if (!isBalanced) {
          action = `Deposit more ${
            apiPool.tokenValueRatio.lt(numberTo18Decimals(poolBalanceThresholds[0]).div(100))
              ? pair.getToken0Symbol()
              : pair.getToken1Symbol()
          } to restore ${bigNumber18DecimalsToPercent(apiPool.potentialApr)} APR`
        } else {
          action = `Deposit until TVL ≥ ${formatPoolMinimumTVL(
            poolMinimumTVL
          )} to restore ${bigNumber18DecimalsToPercent(apiPool.potentialApr)} APR`
        }
      }
    }
  }

  return [disabled, apr, action]
}

function calculateDepositSwap(
  firstValue: CurrencyValue,
  secondValue: CurrencyValue,
  pair?: Pair,
  apiPool?: PoolModel,
  apiTokenPrices?: TokenPrices
): DepositSwap | undefined {
  const symbolA = firstValue.currency.isNative() ? 'WETH' : firstValue.currency.symbol
  const symbolB = secondValue.currency.isNative() ? 'WETH' : secondValue.currency.symbol
  const tokenAPrice = apiTokenPrices && apiTokenPrices[symbolA]
  const tokenBPrice = apiTokenPrices && apiTokenPrices[symbolB]
  if (pair && apiPool && tokenAPrice && tokenBPrice && !tokenAPrice.price.isZero() && !tokenBPrice.price.isZero()) {
    const [token0, token1, price0, price1, balance0, balance1] =
      pair.getToken0Symbol() === firstValue.currency.symbol
        ? [firstValue, secondValue, tokenAPrice, tokenBPrice, pair.getFirstReserve(), pair.getSecondReserve()]
        : [secondValue, firstValue, tokenBPrice, tokenAPrice, pair.getSecondReserve(), pair.getFirstReserve()]

    const token0Amount = normalizeTo18Decimals(token0.value, token0.currency.decimals)
    const token1Amount = normalizeTo18Decimals(token1.value, token1.currency.decimals)
    const token0Balance = normalizeTo18Decimals(balance0.value, balance0.currency.decimals)
    const token1Balance = normalizeTo18Decimals(balance1.value, balance1.currency.decimals)

    const ratio = token1Amount.isZero() ? BN_10_18 : token0Amount.mul(BN_10_18).div(token1Amount)
    const poolRatio = token1Balance.isZero() ? BN_10_18 : token0Balance.mul(BN_10_18).div(token1Balance)

    if (ratio.gt(poolRatio)) {
      const balanced0 = token1Amount.mul(poolRatio).div(BN_10_18)
      const remainder = token0Amount.sub(balanced0)
      if (remainder.gt(BN_ZERO)) {
        const price = price0.price.mul(BN_10_18).div(price1.price)
        const tradeX = remainder.mul(BN_10_18).div(price.mul(poolRatio).div(BN_10_18).add(BN_10_18))
        return {
          volume: tradeX.mul(price0.price).div(BN_10_18),
          swapInToken: token0.currency,
          swapOutToken: token1.currency,
        }
      }
    } else {
      const balanced1 = poolRatio.isZero() ? token1Amount : token0Amount.mul(BN_10_18).div(poolRatio)
      const remainder = token1Amount.sub(balanced1)
      if (remainder.gt(BN_ZERO)) {
        const price = price1.price.mul(BN_10_18).div(price0.price)
        const tradeY = remainder.mul(poolRatio).div(price.add(poolRatio))
        return {
          volume: tradeY.mul(price1.price).div(BN_10_18),
          swapInToken: token1.currency,
          swapOutToken: token0.currency,
        }
      }
    }

    return {
      volume: BN_ZERO,
      swapInToken: token0.currency,
      swapOutToken: token1.currency,
    }
  }
}

function calculateGasRebateDisplay(
  pair?: Pair,
  depositSwap?: DepositSwap,
  apiPool?: PoolModel,
  poolBalanceThresholds?: number[]
) {
  if (pair && depositSwap && apiPool && poolBalanceThresholds) {
    if (depositSwap.volume.gte(numberTo18Decimals(GAS_REBATE_VOLUME))) {
      if (
        (apiPool.tokenValueRatio.lt(numberTo18Decimals(poolBalanceThresholds[0]).div(100)) &&
          pair.getToken0Symbol() === depositSwap.swapInToken.symbol) ||
        (apiPool.tokenValueRatio.gt(numberTo18Decimals(poolBalanceThresholds[1]).div(100)) &&
          pair.getToken1Symbol() !== depositSwap.swapInToken.symbol)
      ) {
        return '95%'
      }
      return '80%'
    }
    return '0%'
  }
  return '-'
}

function getPoolMinimumTVL(pair?: Pair) {
  if (pair) {
    const chainId = pair.getChainId()
    if (chainId) {
      switch (pair.address) {
        case WETH_WBTC[chainId]:
        case WETH_USDC[chainId]:
        case WETH_USDT[chainId]:
        case WETH_DAI[chainId]:
        case WETH_LINK[chainId]:
        case DAI_USDC[chainId!]:
        case DAI_USDT[chainId!]:
        case USDC_USDT[chainId!]:
        default:
          return 0
      }
    }
    return 0
  }
}

function getPoolBalanceThresholds(pair?: Pair) {
  if (pair) {
    const chainId = pair.getChainId()
    if (chainId) {
      switch (pair.address) {
        case WETH_WBTC[chainId]:
        case WETH_USDC[chainId]:
        case WETH_USDT[chainId]:
        case WETH_DAI[chainId]:
        case WETH_LINK[chainId]:
        case DAI_USDC[chainId!]:
        case DAI_USDT[chainId!]:
        case USDC_USDT[chainId!]:
        default:
          return [POOL_BALANCE_LOWER_THRESHOLD_DEFAULT, POOL_BALANCE_UPPER_THRESHOLD_DEFAULT]
      }
    }
  }
}

const Content = styled.div`
  margin-bottom: 20px;
  padding: 0 20px;
`

const Container = styled.div`
  padding: 4px 10px 8px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgb(242, 242, 242);
  background: linear-gradient(0deg, rgba(242, 242, 242, 1) 0%, rgba(239, 239, 239, 1) 100%);
  border: 1px solid #cbcbcb;
  border-radius: ${Borders.Radius};
`

const Left = styled.div`
  > ul {
    margin: 0;
    padding: 0;
    list-style: none;

    > li {
      margin-bottom: 10px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
`

const Right = styled.div`
  flex-basis: 60%;
  flex-shrink: 0;

  > ul {
    margin: 0;
    padding: 0;
    list-style: none;

    > li {
      margin-bottom: 4px;
      display: flex;
      justify-content: space-between;
      align-items: center;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
`

const LeftLabel = styled.strong`
  display: inline-block;
  font-size: 11px;
  font-weight: ${FontWeights.Bold};
  color: ${Colors.DarkGray};
  line-height: 0.6;
  text-transform: uppercase;

  > * {
    line-height: initial;
  }

  > button {
    position: relative;
    top: 2px;
  }
`

const Label = styled.strong`
  margin-right: 5px;
  display: flex;
  align-items: center;
  font-size: 11px;
  font-weight: ${FontWeights.Bold};
  color: ${Colors.DarkGray};
  text-transform: uppercase;

  > svg {
    margin-right: 4px;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    color: ${Colors.DarkGray};
  }
`

const Value = styled.span`
  display: flex;
  align-items: center;
  color: ${Colors.DarkerGray};
  font-size: 11px;
  white-space: nowrap;

  > * {
    white-space: initial;
  }
`

const BigValue = styled.span<{ warning?: boolean }>`
  display: block;
  font-size: 14px;
  color: ${({ warning }) => (warning ? Colors.DarkRed : Colors.DarkerGray)};
  text-transform: uppercase;
`

const Small = styled.small<{ warning?: boolean }>`
  margin-top: 2px;
  display: block;
  min-height: 27px;
  color: ${({ warning }) => (warning ? Colors.DarkRed : Colors.DarkerGray)};
  font-size: 10px;
`
